//* CS 응대 로그 통계 — "무슨 문의가 얼마나 들어오나"를 표 대신 한눈에 보기 위한 집계.
//* 목적은 대시보드가 아니라 **응대 매뉴얼 재료**다. 그래서 건수보다 "어디에 몰리나"를 본다.
//*
//* 답례품(giftCsMemo)·화환(shopOrderCsMemo) 두 로그가 같은 패널을 쓴다. 도메인마다 필드가
//* 다르므로 각 화면이 자기 아이템을 `CsMemoStatRow`로 접어서 넘긴다 — 여기서는 축 이름만 안다.
//* 값이 없는 축은 빈 배열로 나가고, 패널이 그 카드를 통째로 숨긴다.
//*
//* 집계 대상은 조회 조건 전체(최대 2000건)이고 표의 한 페이지가 아니다. 상한에 걸리면
//* 호출부가 `capped`로 잘렸다고 밝힌다 — "전체"라고 말하면 안 되니까.

export interface StatBucket {
  label: string;
  count: number;
  /** 검색창에 넣을 말 — 있으면 클릭으로 그 건들만 다시 조회한다 */
  query?: string;
}

/** 도메인 아이템을 통계가 아는 축으로 접은 것 — 빈 값은 그 축에서 그냥 빠진다 */
export interface CsMemoStatRow {
  createdAt: Date | string;
  byName: string;
  adminOnly?: boolean;
  body: string;
  /** 재응대(같은 건에 두 번 이상)를 세는 키 — 쿠폰/배송/주문 건 단위 */
  axisKey?: string;
  /** 답례품: 쿠폰·배송·주문 */
  unit?: string;
  /** 답례품: 쿠폰형·배송형·혼합 */
  orderType?: string;
  /** 화환: 결제 상태 (답례품은 없음) */
  paymentStatus?: string;
  /** 공통 상태 — 답례품은 교환/배송, 화환은 배송 */
  status?: string;
  /** 답례품: 발송 매체 */
  channel?: string;
  itemName?: string;
  /** 상조(부고브랜드) 표기명 */
  brand?: string;
}

const rank = (map: Map<string, number>, limit?: number): StatBucket[] => {
  const rows = Array.from(map.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
  return limit ? rows.slice(0, limit) : rows;
};

const bump = (map: Map<string, number>, key: string | undefined) => {
  if (!key) return;
  map.set(key, (map.get(key) ?? 0) + 1);
};

const pad = (n: number) => String(n).padStart(2, '0');
const dayKey = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const WEEKDAY = ['일', '월', '화', '수', '목', '금', '토'];

/**
 * 문의 유형 사전 — 매뉴얼의 목차가 될 자리다.
 * 한 메모가 여러 유형에 걸릴 수 있어(환불 + 미수신) 합계는 건수와 다르다. 겹침이 정상.
 * `query`는 그 유형을 다시 뽑아볼 대표 검색어 (서버 q는 부분일치).
 */
const INQUIRY_TYPES: Array<{ label: string; test: RegExp; query: string }> = [
  { label: '환불·취소', test: /환불|취소|반품/, query: '환불' },
  {
    label: '미수신·재발송',
    test: /못\s?받|안\s?왔|안옴|미수신|재발송|다시\s?보내/,
    query: '재발송',
  },
  { label: '교환신청·재선택', test: /교환|재선택|품절|선택.?변경/, query: '교환' },
  { label: '배송·송장', test: /배송|택배|송장|운송장|주소|반송|분실|누락/, query: '배송' },
  {
    label: '결제·입금',
    test: /결제|입금|계좌|카드|영수증|세금계산서|현금영수증/,
    query: '입금',
  },
  { label: '인증·링크', test: /인증|링크|접속|안\s?열|로그인|비밀번호/, query: '인증' },
  { label: '중복·오처리', test: /중복|잘못|오발송|오주문|착오/, query: '중복' },
  { label: '수량·재발행', test: /수량|추가\s?발행|재발행|더\s?보내/, query: '재발행' },
  { label: '문구·리본', test: /문구|리본|근조|경조|이름\s?수정/, query: '문구' },
  { label: '상품문의', test: /유효기간|기한|브랜드|정품|상품\s?문의|바꿔/, query: '유효기간' },
];

//* 자유 키워드에서 걸러낼 말 — 어느 메모에나 나와 정보가 없는 것들
const KEYWORD_STOP = new Set([
  '확인',
  '안내',
  '요청',
  '고객',
  '전화',
  '연락',
  '드림',
  '완료',
  '처리',
  '해서',
  '해주',
  '해드',
  '했습',
  '합니',
  '있습',
  '없습',
  '드렸',
  '드립',
  '관련',
  '부분',
  '내용',
  '이후',
  '다시',
  '그래',
  '하지',
  '때문',
  '정도',
  '대해',
  '통해',
  '경우',
  '문의',
  '답변',
  '설명',
  '말씀',
  '상담',
]);

export interface CsMemoStats {
  total: number;
  /** 집계에 실제로 쓴 건수 (상한에 걸리면 total보다 작다) */
  counted: number;
  days: number;
  perDay: number;
  adminOnly: number;
  /** 같은 건에 메모가 2건 이상 = 한 번에 안 끝난 응대 */
  repeatAxes: number;
  totalAxes: number;
  peakDay?: StatBucket;
  peakHour?: StatBucket;
  byDay: StatBucket[];
  byHour: StatBucket[];
  byWeekday: StatBucket[];
  byUnit: StatBucket[];
  byOrderType: StatBucket[];
  byPaymentStatus: StatBucket[];
  byStatus: StatBucket[];
  byWriter: StatBucket[];
  byChannel: StatBucket[];
  byItem: StatBucket[];
  byBrand: StatBucket[];
  inquiryTypes: StatBucket[];
  keywords: StatBucket[];
}

export const buildCsMemoStats = (rows: CsMemoStatRow[], total: number): CsMemoStats => {
  const day = new Map<string, number>();
  const hour = new Map<string, number>();
  const weekday = new Map<string, number>();
  const unit = new Map<string, number>();
  const orderType = new Map<string, number>();
  const paymentStatus = new Map<string, number>();
  const status = new Map<string, number>();
  const writer = new Map<string, number>();
  const channel = new Map<string, number>();
  const itemName = new Map<string, number>();
  const brand = new Map<string, number>();
  const inquiry = new Map<string, number>();
  const keyword = new Map<string, number>();
  //* 건(축)별 메모 수 — 재응대율의 분모·분자
  const axisMemoCount = new Map<string, number>();

  let adminOnly = 0;

  for (const row of rows) {
    const created = new Date(row.createdAt);
    bump(day, dayKey(created));
    bump(hour, pad(created.getHours()));
    bump(weekday, WEEKDAY[created.getDay()]);
    bump(writer, row.byName);
    if (row.adminOnly) adminOnly += 1;

    bump(unit, row.unit);
    bump(orderType, row.orderType);
    bump(paymentStatus, row.paymentStatus);
    bump(status, row.status);
    bump(channel, row.channel);
    bump(itemName, row.itemName);
    bump(brand, row.brand);

    if (row.axisKey) {
      axisMemoCount.set(row.axisKey, (axisMemoCount.get(row.axisKey) ?? 0) + 1);
    }

    const body = row.body ?? '';
    for (const type of INQUIRY_TYPES) {
      if (type.test.test(body)) bump(inquiry, type.label);
    }

    //* 자유 키워드 — 사전이 놓친 말을 찾는 용도라 형태소 분석 없이 2글자 토큰 빈도만 본다.
    //* 숫자·전화번호는 애초에 토큰에서 빠진다(식별자라 통계 의미 없음).
    const seen = new Set<string>();
    for (const raw of body.split(/[^가-힣a-zA-Z]+/)) {
      const token = raw.length > 6 ? raw.slice(0, 6) : raw;
      if (token.length < 2) continue;
      const head = token.slice(0, 2);
      if (KEYWORD_STOP.has(head) || KEYWORD_STOP.has(token)) continue;
      //* 한 메모 안에서 같은 말을 여러 번 써도 1건 — 긴 메모가 순위를 독식하지 않게
      if (seen.has(token)) continue;
      seen.add(token);
      bump(keyword, token);
    }
  }

  const byDay = Array.from(day.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => a.label.localeCompare(b.label));
  const byHour = Array.from({ length: 24 }, (_, h) => ({
    label: pad(h),
    count: hour.get(pad(h)) ?? 0,
  }));

  const peakDay = [...byDay].sort((a, b) => b.count - a.count)[0];
  const peakHour = [...byHour].sort((a, b) => b.count - a.count)[0];
  const counted = rows.length;
  const days = byDay.length || 1;
  const axes = Array.from(axisMemoCount.values());

  return {
    total,
    counted,
    days,
    perDay: counted / days,
    adminOnly,
    repeatAxes: axes.filter((n) => n > 1).length,
    totalAxes: axes.length,
    ...(peakDay && peakDay.count > 0 ? { peakDay } : {}),
    ...(peakHour && peakHour.count > 0 ? { peakHour } : {}),
    byDay,
    byHour,
    byWeekday: WEEKDAY.map((label) => ({ label, count: weekday.get(label) ?? 0 })),
    byUnit: rank(unit),
    byOrderType: rank(orderType),
    byPaymentStatus: rank(paymentStatus, 8),
    byStatus: rank(status, 8),
    byWriter: rank(writer, 8),
    byChannel: rank(channel),
    byItem: rank(itemName, 6),
    byBrand: rank(brand, 8),
    inquiryTypes: INQUIRY_TYPES.map(({ label, query }) => ({
      label,
      count: inquiry.get(label) ?? 0,
      query,
    }))
      .filter((row) => row.count > 0)
      .sort((a, b) => b.count - a.count),
    keywords: rank(keyword, 16).map((row) => ({ ...row, query: row.label })),
  };
};
